import React from "react";
import { connect } from "react-redux";
import { useFormContext } from "react-hook-form";

import { PriceAndAreaIcon } from "../../../../assets";
import CurrencyDropdown from "../../../../layout/CurrencyDropdown";
import {
  areaMeasurmentOptions,
  EMPTY_STRING,
  DEFAULT_CURRENCY_ID,
  PERCENTAGE_SYMBOL,
  ZERO_VALUE,
  PERCENTAGE_BASE,
} from "../../../../constants";
import CustomSelect from "../../../../shared/components/select/CustomSelect";
import { formatToTwoDecimalPlaces } from "../../../../shared/utility";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const calculateDepositFromPercentage = (price, percentage) => {
  const priceValue = toNumber(price);
  const percentageValue = toNumber(percentage);
  if (
    priceValue === null ||
    percentageValue === null ||
    priceValue <= ZERO_VALUE
  ) {
    return null;
  }
  return Number(
    formatToTwoDecimalPlaces((priceValue * percentageValue) / PERCENTAGE_BASE)
  );
};

const calculatePercentageFromDeposit = (price, deposit) => {
  const priceValue = toNumber(price);
  const depositValue = toNumber(deposit);
  if (
    priceValue === null ||
    depositValue === null ||
    priceValue <= ZERO_VALUE
  ) {
    return null;
  }
  return Number(
    formatToTwoDecimalPlaces((depositValue / priceValue) * PERCENTAGE_BASE)
  );
};

const PriceAndArea = (props) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const selectedCurrency = watch("currencyId");
  const price = watch("price");
  const depositPercentage = watch("depositPercentage");

  const priceRegister = register("price");
  const depositRegister = register("depositAmount");
  const percentageRegister = register("depositPercentage");

  const handleCurrencyChange = (currency) => {
    setValue("currencyId", currency);
  };

  const handlePriceChange = (event) => {
    priceRegister.onChange(event);
    const nextDeposit = calculateDepositFromPercentage(
      event.target.value,
      depositPercentage
    );
    if (nextDeposit !== null) {
      setValue("depositAmount", nextDeposit, { shouldValidate: true });
    }
  };

  const handlePercentageChange = (event) => {
    percentageRegister.onChange(event);
    const nextDeposit = calculateDepositFromPercentage(
      price,
      event.target.value
    );
    if (nextDeposit !== null) {
      setValue("depositAmount", nextDeposit, { shouldValidate: true });
    }
  };

  const handleDepositChange = (event) => {
    depositRegister.onChange(event);
    const nextPercentage = calculatePercentageFromDeposit(
      price,
      event.target.value
    );
    if (nextPercentage !== null) {
      setValue("depositPercentage", nextPercentage, { shouldValidate: true });
    }
  };

  return (
    <div className="create-form-card">
      <div className="form-card-heading">
        <img src={PriceAndAreaIcon} alt={EMPTY_STRING} />
        Price and Area
      </div>
      <div className="d-flex gap-3 area-fields-container">
        <input
          type="text"
          className={`create-list-input w-auto ${
            errors.area ? "border border-danger" : EMPTY_STRING
          }`}
          placeholder="Enter Area Size"
          name="area"
          {...register("area", { required: "Area size is required." })}
        />
        <CustomSelect
          options={areaMeasurmentOptions}
          className="w-50"
          name="areaMeasurementType"
          placeholder="Area"
          showError={false}
          isClearable={false}
          defaultStyle={false}
        />
      </div>
      <div className="price-field-container">
        <div className="d-flex column-gap-3 row-gap-2 flex-wrap">
          <input
            type="text"
            className={`create-list-input w-auto ${
              errors.price ? "border border-danger" : EMPTY_STRING
            }`}
            placeholder="Enter Price"
            name="price"
            {...priceRegister}
            onChange={handlePriceChange}
          />
          <div className="deposit-percentage-input">
            <input
              type="text"
              className={`create-list-input w-auto ${
                errors.depositPercentage ? "border border-danger" : EMPTY_STRING
              }`}
              placeholder="Deposit Percentage"
              name="depositPercentage"
              {...percentageRegister}
              onChange={handlePercentageChange}
            />
            <span className="deposit-percentage-suffix">
              {PERCENTAGE_SYMBOL}
            </span>
          </div>
          <input
            type="text"
            className={`create-list-input w-auto ${
              errors.depositAmount ? "border border-danger" : EMPTY_STRING
            }`}
            placeholder="Enter Deposit Required"
            name="depositAmount"
            {...depositRegister}
            onChange={handleDepositChange}
          />
          <CurrencyDropdown
            buttonClass="create-list-currency"
            allCurrencies={props.allCurrencies}
            className="create-list-input w-auto px-2 py-1"
            selectedCurrency={selectedCurrency ?? DEFAULT_CURRENCY_ID}
            onCurrencyChange={handleCurrencyChange}
          />
        </div>
      </div>
      <div className="instruction ms-0">
        *Our system validates the price. Please enter a realistic price for your
        property that is according to the market rates for the selected
        location.
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  allCurrencies: state.homeReducer.allCurrencies,
});

export default connect(mapStateToProps, null)(PriceAndArea);
